import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import FadeIn from '../shared/FadeIn';
import ScrollRevealText from '../shared/ScrollRevealText';
import VerticalProgress from '../shared/VerticalProgress';
import { getProductFeatures } from '../../data/productFeatures';
import { useLang, useT } from '../../i18n/LanguageContext';
import strings from './ProductPage.i18n';
import './ProductPage.css';

gsap.registerPlugin(ScrollTrigger);

export default function ProductPage({ onNavigate }) {
  const wrapperRef = useRef(null);
  const contentRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [lineProgress, setLineProgress] = useState(0);
  const { lang } = useLang();
  const t = useT(strings);
  const productFeatures = getProductFeatures(lang);

  useEffect(() => {
    if (!wrapperRef.current || !contentRef.current) return;

    // Mobile: no scroll tracking or snap — just natural scroll
    if (window.innerWidth <= 768) {
      return;
    }

    // Desktop: fullPage-style locked scroll — one section per scroll event
    const wrapper = wrapperRef.current;
    const rows = contentRef.current.querySelectorAll('.pd-row');
    const n = rows.length;
    if (n < 2) return;

    let currentIdx = 0;
    let isAnimating = false;
    let hasEntered = false; // track if user has entered the section

    const getRowTop = (i) => {
      const row = rows[i];
      if (!row) return 0;
      return row.getBoundingClientRect().top + window.scrollY;
    };

    // Scroll-triggered: one section at a time, locked during animation
    const goToSection = (idx) => {
      if (idx < 0 || idx >= n || idx === currentIdx || isAnimating) return;
      isAnimating = true;
      currentIdx = idx;
      setActiveIndex(idx);
      setLineProgress(idx / (n - 1));
      const target = getRowTop(idx);
      const scroll = { y: window.scrollY };
      gsap.to(scroll, {
        y: target,
        duration: 0.12,
        ease: 'none',
        onUpdate: () => window.scrollTo(0, scroll.y),
        onComplete: () => {
          setTimeout(() => { isAnimating = false; }, 40);
        },
      });
    };

    // TOC click: instant jump, no animation guard against same index
    const jumpToSection = (idx) => {
      if (idx < 0 || idx >= n || isAnimating) return;
      isAnimating = true;
      currentIdx = idx;
      setActiveIndex(idx);
      setLineProgress(idx / (n - 1));
      const target = getRowTop(idx);
      window.scrollTo(0, target);
      setTimeout(() => { isAnimating = false; }, 50);
    };

    // Wheel handler — intercept and move one section at a time
    const handleWheel = (e) => {
      const wrapperRect = wrapper.getBoundingClientRect();
      const inSection = wrapperRect.top <= 10 && wrapperRect.bottom >= window.innerHeight - 10;

      if (!inSection) {
        hasEntered = false;
        return;
      }

      // First entry: snap to nearest row without animation
      if (!hasEntered) {
        hasEntered = true;
        let bestIdx = 0;
        let bestDist = Infinity;
        rows.forEach((row, i) => {
          const dist = Math.abs(row.getBoundingClientRect().top);
          if (dist < bestDist) { bestDist = dist; bestIdx = i; }
        });
        currentIdx = bestIdx;
        setActiveIndex(bestIdx);
        setLineProgress(bestIdx / (n - 1));
        // Snap to it
        const target = getRowTop(bestIdx);
        window.scrollTo(0, target);
      }

      // Allow natural scroll out at boundaries
      if (currentIdx === 0 && e.deltaY < 0) { hasEntered = false; return; }
      if (currentIdx === n - 1 && e.deltaY > 0) { hasEntered = false; return; }

      e.preventDefault();
      if (isAnimating) return;

      if (e.deltaY > 0) {
        goToSection(currentIdx + 1);
      } else if (e.deltaY < 0) {
        goToSection(currentIdx - 1);
      }
    };

    // Touch handler for swipes
    let touchStartY = 0;
    const handleTouchStart = (e) => {
      touchStartY = e.touches[0].clientY;
    };
    const handleTouchEnd = (e) => {
      const wrapperRect = wrapper.getBoundingClientRect();
      const inSection = wrapperRect.top <= 10 && wrapperRect.bottom >= window.innerHeight - 10;
      if (!inSection || isAnimating) return;

      const diff = touchStartY - e.changedTouches[0].clientY;
      if (Math.abs(diff) < 30) return;

      if (diff > 0 && currentIdx < n - 1) {
        e.preventDefault();
        goToSection(currentIdx + 1);
      } else if (diff < 0 && currentIdx > 0) {
        e.preventDefault();
        goToSection(currentIdx - 1);
      }
    };

    // Expose jumpToSection for TOC clicks
    wrapper._goToSection = jumpToSection;

    window.addEventListener('wheel', handleWheel, { passive: false });
    wrapper.addEventListener('touchstart', handleTouchStart, { passive: true });
    wrapper.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleWheel);
      wrapper.removeEventListener('touchstart', handleTouchStart);
      wrapper.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  return (
    <div className="product-page">
      {/* ---- Section 1: Product Hero ---- */}
      <section className="product-hero gradient-hero">
        <div className="section" style={{ textAlign: 'center' }}>
          <FadeIn>
            <p className="section-label">{t.label}</p>
          </FadeIn>
          <FadeIn delay={100}>
            <h1 className="section-title" style={{ fontSize: '52px' }}>
              {t.titlePart1}<span className="text-accent">{t.titleAccent}</span>
            </h1>
          </FadeIn>
          <FadeIn delay={200}>
            <ScrollRevealText className="section-subtitle" style={{ margin: '0 auto' }}>
              {t.subtitle}
            </ScrollRevealText>
          </FadeIn>
        </div>
      </section>

      {/* ---- Section 2: Feature Grid ---- */}
      <section className="product-feature-grid-section" style={{ position: 'relative' }}>
        <VerticalProgress />
        <div className="section">
          <div className="product-feature-grid">
            {productFeatures.map((feature, i) => (
              <FadeIn key={feature.id} delay={i * 80}>
                <div
                  className="product-feature-card"
                  onClick={() => onNavigate && onNavigate('feature-' + feature.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onNavigate && onNavigate('feature-' + feature.id);
                    }
                  }}
                >
                  <div
                    className="product-feature-card-icon"
                    style={{ background: feature.iconBg }}
                  >
                    {feature.icon}
                  </div>
                  <h3 className="product-feature-card-title">{feature.title}</h3>
                  <p className="product-feature-card-desc">{feature.shortDesc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ---- Section 3: Pinned Snap Feature Details ---- */}
      <div className="pd-section-wrapper" data-nav-theme="dark" ref={wrapperRef}>
        <section className="pd-section" ref={contentRef}>
          {/* Left sidebar */}
          <div className="pd-sidebar">
            <div className="pd-sidebar__inner">
              <div className="pd-line">
                <div className="pd-line__track" />
                <div className="pd-line__fill" style={{ height: `${lineProgress * 100}%` }} />
                <div className="pd-line__dot" style={{ top: `${lineProgress * 100}%` }} />
              </div>
              <div className="pd-toc">
                {productFeatures.map((feature, i) => (
                  <button
                    key={feature.id}
                    className={`pd-toc__item${i === activeIndex ? ' pd-toc__item--active' : ''}`}
                    onClick={() => {
                      const wrapper = wrapperRef.current;
                      if (wrapper?._goToSection) {
                        // Force transition even to the same index
                        wrapper._goToSection(i);
                      }
                    }}
                  >
                    {feature.title}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right content: stacked feature rows */}
          <div className="pd-content">
            {productFeatures.map((feature) => {
              const firstSection = feature.sections[0];
              return (
                <div className="pd-row" key={feature.id}>
                  <div className="pd-row__text">
                    <h2 className="pd-row__title">{feature.title}</h2>
                    <p className="pd-row__desc">{firstSection.description}</p>
                    <button
                      className="pd-row__link"
                      onClick={() => onNavigate && onNavigate('feature-' + feature.id)}
                    >
                      {t.learnMore}
                    </button>
                  </div>
                  <div className="pd-row__image-wrap">
                    <img
                      className="pd-row__image"
                      src={firstSection.screenshot}
                      alt={firstSection.title}
                      loading="lazy"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {/* ---- Section 4: CTA ---- */}
      <section className="product-cta" data-nav-theme="dark">
        <img
          className="product-cta__img"
          src={process.env.PUBLIC_URL + '/images/ctasketch.png'}
          alt=""
        />
        <div className="product-cta__text">
          <h2 className="product-cta__title">
            {t.ctaTitle}
          </h2>
          <p className="product-cta__desc">
            {t.ctaDesc}
          </p>
          <div className="product-cta__buttons">
            <button className="product-cta__btn product-cta__btn--primary" onClick={() => window.open('https://wa.me/601163688943?text=Hi%2C%20I%20would%20like%20to%20get%20started%20with%20SAPOT%20AI', '_blank')}>
              {t.ctaStart}
            </button>
            <button className="product-cta__btn product-cta__btn--secondary" onClick={() => window.open('https://wa.me/601163688943?text=Hi%2C%20I%20would%20like%20to%20get%20a%20demo%20of%20SAPOT%20AI', '_blank')}>
              {t.ctaDemo}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
